import React, { Component, PropTypes } from 'react'
import { connect } from 'react-redux'
import { browserHistory } from 'react-router'
import { CardTitle, CardText } from 'material-ui/Card'
import Dialog from 'material-ui/Dialog'
import { setSnackbarMessage } from '../../../actions/snackbar'
import { formatCurrency } from '../../../formatters'
import Actions from './actions'
import { get, noop, set, debounce } from 'lodash'

import {
  FlatButton,
  RaisedButton,
  CircularProgress
} from 'material-ui'

import api from '../../../api'
import FieldList from '../../fieldList'

const loadingIndicatorTimeout = 100

class Create extends Component<any, any> {
  constructor (props) {
    super(props)
    this.state = {
      resource: props.resource || {},
      errors: {},
      autocompleteText: {},
      autocompleteResults: {},
      resourceFiles: {},
      submitting: false
    }
// @ts-ignore
    this.debouncedAutocompleteUpdate = debounce(this.performAutocompleteUpdate.bind(this), 500)
  }

  componentWillReceiveProps (nextProps) {
    if (nextProps.resource) {
      this.setState({
        resource: nextProps.resource
      })
    }
  }

  handleAutocompleteFocus (field) {
    if (get(this.state.autocompleteResults, [field.references, field.prop]) === undefined) {
      this.handleAutocompleteUpdateInput(field, '')
    }
  }

  handleAutocompleteUpdateInput (field, searchText) {
    const { resource } = this.state

    // Any change to search text means the original selection is irrelevant.
    if (resource[field.prop]) {
      this.setState(state => ({
        resource: { ...state.resource, [field.prop]: null }
      }))
    }

    // Any change to search text means errors for this field are irrelevant.
    if (this.state.errors[field.prop]) {
      this.setState(state => ({
        errors: { ...state.errors, [field.prop]: [] }
      }))
    }

    // Need to update autocomplete search text in our state on every change so
    // that when we want to clear the field we are passing the Autocomplete a
    // different `searchText` prop.
    const autocompletePath = [field.references, field.prop]
    this.setState(state => ({
      ...state,
      autocompleteText: set(
        state.autocompleteText,
        autocompletePath,
        searchText
      )
    }))

    // Debounce the API call
// @ts-ignore
    this.debouncedAutocompleteUpdate(field, searchText)
  }

  performAutocompleteUpdate (field, searchText) {
    const { resource } = this.state
    const apiQuery = {
      filters: Object.assign({
        [`${field.referenceProp}_ilike`]: searchText
      }, field.customFilters),
      limit: 100,
      order: [{ column: 'id', direction: 'asc' }]
    }

    const { query = {} } = field

    if (query.filters) {
      const filters = typeof query.filters === 'function'
        ? query.filters(resource)
        : query.filters

      apiQuery.filters = Object.assign({ }, apiQuery.filters, filters)
    }

    if (query.order) {
      apiQuery.order = query.order
    }

    api
      .get(field.references, { body: apiQuery })
      .then(res => res.json())
      .then(collection => {
        this.setState({
          autocompleteResults: Object.assign({}, this.state.autocompleteResults, {
            [field.references]: Object.assign({}, this.state.autocompleteResults[field.references], {
              [field.prop]: field.dataHydrator ? field.dataHydrator(collection) : collection
            })
          })
        })
      })
  }

  handleAutocompleteSelection (field, chosenRequest, selectedIndex) {
    if (selectedIndex === -1) {
      // enter pressed in the autocomplete field
      if (field.rejectCustomValues) {
        this.setState(state => ({
          errors: { ...state.errors, [field.prop]: ['Must choose a valid option'] }
        }))
      }
      return
    }

    this.setState({
      resource: Object.assign({}, this.state.resource, {
        [field.prop]: chosenRequest[field.referenceValue || 'id']
      }),
      autocompleteText: set(
        this.state.autocompleteText,
        [field.references, field.prop],
        chosenRequest[field.displayProp]
      )
    })
  }

  handleValueChange (field, e, value, index = null) {
    const newValue = field.filter ? field.filter(value, this.state.resource) : value

    this.setState({
      resource: Object.assign(this.state.resource, {
        [field.prop]: newValue
      })
    })
  }

  handleFileChange (field, e) {
    const formData = new window.FormData()
    const file = e.target.files[0]
    formData.append(field.prop, file, file.name)
    this.setState({
      resourceFiles: { ...this.state.resourceFiles, [field.prop]: { formData, file } }
    })
  }

  handleCancel () {
    const { cancelRedirectPath, onCancel } = this.props

    onCancel()

    if (typeof cancelRedirectPath === 'function') {
      browserHistory.push(cancelRedirectPath())
    } else if (typeof cancelRedirectPath === 'string') {
      browserHistory.push(cancelRedirectPath)
    } else {
      browserHistory.goBack()
    }
  }

  startSubmit () {
// @ts-ignore
    this.timeout = setTimeout(() => {
      this.setState({ submitting: true })
    }, loadingIndicatorTimeout)
  }

  stopSubmit () {
    this.setState({ submitting: false })
// @ts-ignore
    if (this.timeout) {
// @ts-ignore
      clearTimeout(this.timeout)
    }
  }

  handleSubmit (e) {
    e && e.preventDefault()

    this.startSubmit()

    api
      .post(this.props.apiPath, { body: this.state.resource })
      .then(res => {
        res.json().then(data => {
          if (!res.ok) {
            this.stopSubmit()
            this.setState({
              errors: data.errors || {},
              message: data.message
            })
            return
          }

          this.uploadFiles(data).then(() => {
            this.stopSubmit()
            const snackbar = this.props.onSnackbarMessage
            const resourceNumbers = Array.isArray(data) ? data.map(datum => `#${datum.id}`).join(', ') : `#${data.id}`
            const warning = Array.isArray(data)
              ? data.filter(datum => datum.warning).map(datum => `#${datum.id}: ${datum.warning}`).join('; ')
              : data.warning
            snackbar && snackbar(warning || `Successfully created ${resourceNumbers}`)
            this.props.onResourceCreated(data)
            this.successRedirect(data)
          })
        })
      })
  }

  successRedirect (resource) {
    const { successRedirectPath } = this.props

    if (typeof successRedirectPath === 'function') {
      browserHistory.push(successRedirectPath(resource))
    } else {
      browserHistory.push(successRedirectPath)
    }
  }

  uploadFiles (data) {
    const fields = Object.keys(this.state.resourceFiles)
    if (fields.length) {
      return Promise.all(fields.map(field => {
        const path = `${this.props.apiPath}/${data.id}/${field}`
        const body = this.state.resourceFiles[field].formData
        return api.patchFile(path, { body })
      }))
    } else {
      return Promise.resolve()
    }
  }

  handleChipInputAdd (field, chip) {
    this.setState(prevState => {
      const chips = (this.state.resource[field.prop] || []).concat(chip)

      return {
        ...prevState,
        resource: {
          ...this.state.resource,
          [field.prop]: chips
        }
      }
    })
  }

  handleChipInputDelete (field, chip) {
    const index = this.state.resource[field.prop].indexOf(chip)

    if (index === -1) {
      return
    }

    this.setState({
      resource: Object.assign({}, this.state.resource, {
        [field.prop]: [
          ...this.state.resource[field.prop].slice(0, index),
          ...this.state.resource[field.prop].slice(index + 1)
        ]
      })
    })
  }

  handleAcknowledgeErrorMessage () {
    this.setState({ message: null })
  }

  render () {
    const formStyle = {
      maxWidth: 700
    }

    const buttonStyle = {
      margin: '1em 1em 0 0'
    }

    const cardTextStyle = {
      light: {
        display: 'inline',
        padding: '0',
        fontSize: '16px',
        color: 'gray'
      },

      bold: {
        display: 'inline',
        padding: '0',
        fontSize: '16px',
        color: 'gray',
        fontWeight: '500'
      }
    }

    return (
      <div>
        <Actions
          buildMenuItems={this.props.buildMenuItems}
        />
        <CardTitle title={`${this.props.title}`} style={{ paddingLeft: 0 }} />
        {
          this.props.textList &&
          this.props.textList.map(item => (
            <div>
              <CardText style={cardTextStyle.bold}>{item.text}</CardText>
              <CardText style={cardTextStyle.light}>{formatCurrency(item.value)}</CardText>
            </div>
          ))
        }
        <form onSubmit={this.handleSubmit.bind(this)} style={formStyle}>
          <FieldList
            fields={this.props.fields}
            autocompleteResults={this.state.autocompleteResults}
            autocompleteText={this.state.autocompleteText}
            handleAutocompleteSelection={this.handleAutocompleteSelection.bind(this)}
            handleAutocompleteUpdateInput={this.handleAutocompleteUpdateInput.bind(this)}
            handleAutocompleteFocus={this.handleAutocompleteFocus.bind(this)}
            handleChipInputAdd={this.handleChipInputAdd.bind(this)}
            handleChipInputDelete={this.handleChipInputDelete.bind(this)}
            resource={this.state.resource}
            resourceFiles={this.state.resourceFiles}
            errors={this.state.errors}
            onChange={this.handleValueChange.bind(this)}
            handleFileChange={this.handleFileChange.bind(this)}
          />

          <RaisedButton
            label='Create'
            primary
            onClick={this.handleSubmit.bind(this)}
            style={buttonStyle}
            labelPosition='before'
            icon={this.state.submitting ? <CircularProgress size={20} thickness={2} /> : null}
            disabled={this.state.submitting}
          />
          <RaisedButton label='Cancel' onClick={this.handleCancel.bind(this)} style={buttonStyle} />

        </form>

        <Dialog
          title='Error'
          modal={false}
          open={!!this.state.message}
          actions={[
            <FlatButton
              label='OK'
              primary
              onClick={this.handleAcknowledgeErrorMessage.bind(this)}
            />
          ]}
          onRequestClose={this.handleAcknowledgeErrorMessage.bind(this)}
        >
          {this.state.message}
        </Dialog>
      </div>
    )
  }
}

// @ts-ignore
Create.propTypes = {
  title: PropTypes.string.isRequired,
  fields: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.object,
      PropTypes.arrayOf(PropTypes.object)
    ])
  ).isRequired,
  successRedirectPath: PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired,
  cancelRedirectPath: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  onSnackbarMessage: PropTypes.func.isRequired,
  apiPath: PropTypes.string.isRequired,
  resource: PropTypes.object,
  onResourceCreated: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
}

// @ts-ignore
Create.defaultProps = {
  onResourceCreated: noop,
  onCancel: noop
}

const mapStateToProps = (state) => ({})

const mapDispatchToProps = (dispatch) => ({
  onSnackbarMessage: (msg) => {
    dispatch(setSnackbarMessage(msg))
  }
})

export default connect(mapStateToProps, mapDispatchToProps)(Create as any) as React.ComponentType<any>
